import { BALANCE } from '../config/balance';
import type { RngStream } from '../core/rng';
import { dist, normalize } from '../core/vec';
import type { Player } from './player';

export interface Gem {
  active: boolean;
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  vx: number;
  vy: number;
  value: number;
  flying: boolean;
  trailT: number;
  /** 进化核心（精英掉落；拾取触发就绪进化或折算金币）。 */
  core: boolean;
  /** 回血心（拾取回血，不给经验）。 */
  heart: boolean;
}

export function createGem(): Gem {
  return {
    active: false,
    x: 0,
    y: 0,
    prevX: 0,
    prevY: 0,
    vx: 0,
    vy: 0,
    value: 1,
    flying: false,
    trailT: 0,
    core: false,
    heart: false,
  };
}

/** 掉落：用 drops 流给一个随机弹出方向。 */
export function spawnGem(g: Gem, x: number, y: number, value: number, rng: RngStream): void {
  const a = rng.range(0, Math.PI * 2);
  const sp = rng.range(BALANCE.xp.popSpeedMin, BALANCE.xp.popSpeedMax);
  g.active = true;
  g.x = x;
  g.y = y;
  g.prevX = x;
  g.prevY = y;
  g.vx = Math.cos(a) * sp;
  g.vy = Math.sin(a) * sp;
  g.value = value;
  g.flying = false;
  g.trailT = 0;
  g.core = false;
  g.heart = false;
}

/** 进化核心掉落（不弹出太远，吸附规则同宝石）。 */
export function spawnCore(g: Gem, x: number, y: number): void {
  g.active = true;
  g.x = x; g.y = y;
  g.prevX = x; g.prevY = y;
  g.vx = 0; g.vy = 0;
  g.value = 0;
  g.flying = false;
  g.trailT = 0;
  g.core = true;
  g.heart = false;
}

/** 回血心掉落（轻微弹出，吸附规则同宝石）。 */
export function spawnHeart(g: Gem, x: number, y: number, rng: RngStream): void {
  const a = rng.range(0, Math.PI * 2);
  const sp = rng.range(40, 90);
  g.active = true;
  g.x = x; g.y = y;
  g.prevX = x; g.prevY = y;
  g.vx = Math.cos(a) * sp;
  g.vy = Math.sin(a) * sp;
  g.value = 0;
  g.flying = false;
  g.trailT = 0;
  g.core = false;
  g.heart = true;
}

/**
 * 更新宝石。返回 true 表示本步被玩家拾取。
 * 拾取轨迹粒子由引擎按 trailT 节拍补发。
 */
export function updateGem(g: Gem, dt: number, player: Player): boolean {
  g.prevX = g.x;
  g.prevY = g.y;
  const x = BALANCE.xp;

  if (!g.flying) {
    g.x += g.vx * dt;
    g.y += g.vy * dt;
    const d = Math.exp(-x.popDamp * dt);
    g.vx *= d;
    g.vy *= d;
    if (dist(g.x, g.y, player.x, player.y) < player.stats.pickupRadius) {
      g.flying = true;
      g.vx = 0;
      g.vy = 0;
    }
    return false;
  }

  const n = normalize(player.x - g.x, player.y - g.y);
  const sp = Math.min(x.flyMaxSpeed, Math.hypot(g.vx, g.vy) + x.flyAccel * dt);
  g.vx = n.x * sp;
  g.vy = n.y * sp;
  g.x += g.vx * dt;
  g.y += g.vy * dt;
  g.trailT -= dt;

  return isCaught(g, player);
}

function isCaught(g: Gem, player: Player): boolean {
  return dist(g.x, g.y, player.x, player.y) < BALANCE.player.radius + 7;
}
